import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Upload, Avatar, Tabs, List, Tag, Modal, Descriptions, message, Row, Col, Steps, Rate, Empty } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, IdcardOutlined, PlusOutlined, EditOutlined, SafetyOutlined, CustomerServiceOutlined, CarOutlined, EnvironmentOutlined, ClockCircleOutlined, DollarOutlined, StarOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { updateUser } from '../../store/slices/authSlice';
import { fetchOrdersSuccess, cancelOrder } from '../../store/slices/orderSlice';
import { fetchRideOrdersSuccess, cancelRideOrder } from '../../store/slices/rideSlice';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const ProfilePage: React.FC = () => {
  const [form] = Form.useForm();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { orders } = useSelector((state: RootState) => state.order);
  const { orders: rideOrders } = useSelector((state: RootState) => state.ride);

  // サンプルデータ
  const [walletData] = useState({
    balance: 520.50,
    deposits: 5000,
    points: 1280,
  });

  // サンプル注文データ
  const mockRentalOrders = [
    {
      id: '1',
      carId: '1',
      carInfo: {
        brand: 'フォルクスワーゲン',
        model: 'Polo',
        plateNumber: '品川 330 あ 1234',
      },
      pickupDate: '2024-02-01 09:00:00',
      returnDate: '2024-02-03 18:00:00',
      pickupLocation: '新宿店',
      returnLocation: '新宿店',
      totalAmount: 17600,
      deposit: 50000,
      status: 'confirmed' as const,
      paymentStatus: 'paid' as const,
      createdAt: '2024-01-28 14:30:00',
    },
    {
      id: '2',
      carId: '2',
      carInfo: {
        brand: 'ホンダ',
        model: 'シビック',
        plateNumber: '品川 330 い 5678',
      },
      pickupDate: '2024-01-25 10:00:00',
      returnDate: '2024-01-27 20:00:00',
      pickupLocation: '渋谷店',
      returnLocation: '羽田空港店',
      totalAmount: 23600,
      deposit: 75000,
      status: 'returned' as const,
      paymentStatus: 'paid' as const,
      createdAt: '2024-01-20 16:45:00',
    },
  ];

  const mockRideOrders = [
    {
      id: '1',
      serviceType: 'carpool' as const,
      passengerId: 'user1',
      driverId: 'driver1',
      pickupLocation: {
        city: '東京',
        address: '東京駅',
        coordinates: [139.7673, 35.6812] as [number, number],
      },
      dropoffLocation: {
        city: '横浜',
        address: '横浜駅',
        coordinates: [139.6380, 35.4657] as [number, number],
      },
      scheduledTime: '2024-02-01 14:30:00',
      passengerCount: 1,
      totalAmount: 4500,
      status: 'completed' as const,
      paymentStatus: 'paid' as const,
      driverInfo: {
        name: '田中さん',
        phone: '090-****-5678',
        vehiclePlate: '品川 500 あ 8888',
        rating: 4.8,
      },
      createdAt: '2024-01-28 10:00:00',
      updatedAt: '2024-02-01 16:30:00',
    },
    {
      id: '2',
      serviceType: 'charter' as const,
      passengerId: 'user1',
      driverId: 'driver2',
      pickupLocation: {
        city: '東京',
        address: '新宿駅',
        coordinates: [139.7006, 35.6896] as [number, number],
      },
      dropoffLocation: {
        city: '大阪',
        address: '大阪駅',
        coordinates: [135.4959, 34.7024] as [number, number],
      },
      scheduledTime: '2024-02-05 09:00:00',
      passengerCount: 3,
      vehicleType: 'business7' as const,
      priceMultiplier: 1.2,
      totalAmount: 45600,
      status: 'accepted' as const,
      paymentStatus: 'paid' as const,
      driverInfo: {
        name: '佐藤さん',
        phone: '090-****-1234',
        vehiclePlate: '品川 500 い 9999',
        rating: 4.9,
      },
      note: 'チャイルドシートが必要です',
      createdAt: '2024-02-01 15:30:00',
      updatedAt: '2024-02-01 15:45:00',
    },
  ];

  useEffect(() => {
    // 注文データの取得をシミュレート
    dispatch(fetchOrdersSuccess(mockRentalOrders));
    dispatch(fetchRideOrdersSuccess(mockRideOrders));
  }, [dispatch]);

  const [emergencyContacts] = useState([
    {
      id: '1',
      name: '田中太郎',
      phone: '090-1234-5678',
      relationship: '配偶者',
    },
    {
      id: '2',
      name: '佐藤花子',
      phone: '080-9876-5432',
      relationship: '友人',
    },
  ]);

  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderDetailVisible, setOrderDetailVisible] = useState(false);

  const handleUpdateProfile = (values: any) => {
    dispatch(updateUser(values));
    message.success('個人情報を更新しました');
  };

  // レンタル注文関連関数
  const getOrderStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      confirmed: 'blue',
      picked_up: 'green',
      returned: 'purple',
      cancelled: 'red',
    };
    return colorMap[status] || 'default';
  };

  const getOrderStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      pending: '確認待ち',
      confirmed: '確定済み',
      picked_up: '使用中',
      returned: '返却済み',
      cancelled: 'キャンセル済み',
    };
    return textMap[status] || status;
  };

  // 配車注文関連関数
  const getRideStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      accepted: 'blue',
      picked_up: 'green',
      completed: 'purple',
      cancelled: 'red',
    };
    return colorMap[status] || 'default';
  };

  const getRideStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      pending: '受付待ち',
      accepted: 'ドライバーが受付',
      picked_up: '移動中',
      completed: '完了',
      cancelled: 'キャンセル済み',
    };
    return textMap[status] || status;
  };

  const getServiceTypeText = (type: string) => {
    return type === 'carpool' ? '相乗り' : '貸切';
  };

  const getServiceTypeColor = (type: string) => {
    return type === 'carpool' ? 'blue' : 'orange';
  };

  const handleViewOrderDetail = (order: any, type: 'rental' | 'ride') => {
    setSelectedOrder({ ...order, orderType: type });
    setOrderDetailVisible(true);
  };

  const handleCancelOrder = (orderId: string, type: 'rental' | 'ride') => {
    Modal.confirm({
      title: '注文キャンセルの確認',
      content: '注文をキャンセルするとキャンセル料が発生する可能性があります。本当にキャンセルしますか？',
      onOk: () => {
        if (type === 'rental') {
          dispatch(cancelOrder(orderId));
        } else {
          dispatch(cancelRideOrder(orderId));
        }
        message.success('注文をキャンセルしました');
      },
    });
  };

  const handleUploadLicense = (info: any) => {
    if (info.file.status === 'done') {
      message.success('運転免許証をアップロードしました');
      setUploadModalVisible(false);
    }
  };

  const uploadProps = {
    name: 'file',
    action: '/api/upload',
    onChange: handleUploadLicense,
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <div className="container">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <Avatar
                  size={80}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>
                  {user?.profile?.firstName && user?.profile?.lastName 
                    ? `${user.profile.lastName} ${user.profile.firstName}`
                    : user?.email || 'ユーザー名未設定'
                  }
                </h3>
                <p style={{ color: '#666' }}>{user?.phone || user?.email}</p>
                <Tag color={user?.status === 'active' ? 'green' : 'orange'}>
                  {user?.status === 'active' ? 'アカウント有効' : 'アカウント無効'}
                </Tag>
              </div>
              <Button
                type="primary"
                icon={<EditOutlined />}
                block
                onClick={() => setUploadModalVisible(true)}
              >
運転免許証のアップロード
              </Button>
            </Card>

            {/* ウォレット情報 */}
            <Card title="マイウォレット" style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ padding: 16, background: '#f6ffed', borderRadius: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                        ¥{walletData.balance.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        残高
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ padding: 16, background: '#fff7e6', borderRadius: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>
                        ¥{walletData.deposits.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        保証金
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ padding: 16, background: '#e6f7ff', borderRadius: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                        {walletData.points.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        ポイント
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card>
              <Tabs defaultActiveKey="profile">
                <TabPane tab="個人情報" key="profile">
                  <Form
                    form={form}
                    onFinish={handleUpdateProfile}
                    layout="vertical"
                    initialValues={{
                      firstName: user?.profile?.firstName,
                      lastName: user?.profile?.lastName,
                      phone: user?.phone,
                      email: '',
                      idCard: '',
                      licenseNumber: '',
                    }}
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="firstName"
                          label="名前"
                          rules={[{ required: true, message: '名前を入力してください' }]}
                        >
                          <Input prefix={<UserOutlined />} placeholder="ニックネームを入力" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="phone"
                          label="電話番号"
                          rules={[
                            { required: true, message: '電話番号を入力してください' },
                            { pattern: /^0\d{1,4}-\d{1,4}-\d{4}$|^0\d{9,10}$/, message: '正しい電話番号を入力してください' }
                          ]}
                        >
                          <Input prefix={<PhoneOutlined />} placeholder="電話番号を入力" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="email"
                          label="メールアドレス"
                          rules={[{ type: 'email', message: '正しいメールアドレスを入力してください' }]}
                        >
                          <Input prefix={<MailOutlined />} placeholder="メールアドレスを入力" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="idCard"
                          label="マイナンバーカード番号"
                        >
                          <Input prefix={<IdcardOutlined />} placeholder="マイナンバーカード番号を入力" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="licenseNumber"
                      label="運転免許証番号"
                    >
                      <Input placeholder="運転免許証番号を入力" />
                    </Form.Item>

                    <Form.Item>
                      <Button type="primary" htmlType="submit">
                        変更を保存
                      </Button>
                    </Form.Item>
                  </Form>
                </TabPane>

                <TabPane tab="マイ注文" key="orders">
                  <Tabs defaultActiveKey="rental">
                    <TabPane tab="レンタカー注文" key="rental">
                      {orders.length === 0 ? (
                        <Empty description="レンタカー注文はありません" />
                      ) : (
                        <List
                          dataSource={orders}
                          renderItem={(order) => (
                            <List.Item>
                              <Card
                                style={{ width: '100%' }}
                                title={
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>注文号：{order.id}</span>
                                    <Tag color={getOrderStatusColor(order.status)}>
                                      {getOrderStatusText(order.status)}
                                    </Tag>
                                  </div>
                                }
                                actions={[
                                  <Button type="link" onClick={() => handleViewOrderDetail(order, 'rental')}>
                                    確認詳細
                                  </Button>,
                                  ...(order.status === 'pending' || order.status === 'confirmed' ? [
                                    <Button danger type="link" onClick={() => handleCancelOrder(order.id, 'rental')}>
                                      キャンセル注文
                                    </Button>
                                  ] : []),
                                ]}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <div>
                                    <h4 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
                                      <CarOutlined style={{ marginRight: 8 }} />
                                      {order.carInfo.brand} {order.carInfo.model}
                                    </h4>
                                    <p style={{ margin: '4px 0', color: '#666' }}>
                                      车牌号：{order.carInfo.plateNumber}
                                    </p>
                                    <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                                      <EnvironmentOutlined style={{ marginRight: 4 }} />
                                      {order.pickupLocation} → {order.returnLocation}
                                    </p>
                                    <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                                      {dayjs(order.pickupDate).format('MM-DD HH:mm')} - {dayjs(order.returnDate).format('MM-DD HH:mm')}
                                    </p>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff', display: 'flex', alignItems: 'center' }}>
                                      <DollarOutlined style={{ marginRight: 4 }} />
                                      ¥{order.totalAmount.toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                      保証金：¥{order.deposit.toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                      注文日時：{dayjs(order.createdAt).format('MM-DD HH:mm')}
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </List.Item>
                          )}
                        />
                      )}
                    </TabPane>

                    <TabPane tab="配車注文" key="ride">
                      {rideOrders.length === 0 ? (
                        <Empty description="配車注文はありません" />
                      ) : (
                        <List
                          dataSource={rideOrders}
                          renderItem={(order) => (
                            <List.Item>
                              <Card
                                style={{ width: '100%' }}
                                title={
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <span style={{ marginRight: 8 }}>注文番号：{order.id}</span>
                                      <Tag color={getServiceTypeColor(order.serviceType)}>
                                        {getServiceTypeText(order.serviceType)}
                                      </Tag>
                                    </div>
                                    <Tag color={getRideStatusColor(order.status)}>
                                      {getRideStatusText(order.status)}
                                    </Tag>
                                  </div>
                                }
                                actions={[
                                  <Button type="link" onClick={() => handleViewOrderDetail(order, 'ride')}>
                                    詳細を見る
                                  </Button>,
                                  ...(order.status === 'pending' || order.status === 'accepted' ? [
                                    <Button danger type="link" onClick={() => handleCancelOrder(order.id, 'ride')}>
                                      注文キャンセル
                                    </Button>
                                  ] : []),
                                ]}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                      <EnvironmentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                                      <span>{order.pickupLocation.address}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                      <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                      <span>{order.dropoffLocation.address}</span>
                                    </div>
                                    <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                                      {dayjs(order.scheduledTime).format('YYYY-MM-DD HH:mm')}
                                    </p>
                                    {order.driverInfo && (
                                      <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                                        <CarOutlined style={{ marginRight: 4 }} />
                                        ドライバー：{order.driverInfo.name} · {order.driverInfo.vehiclePlate} · ⭐{order.driverInfo.rating}
                                      </p>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                                      ¥{order.totalAmount.toLocaleString()}
                                    </div>
                                    {order.priceMultiplier && order.priceMultiplier > 1 && (
                                      <div style={{ fontSize: 12, color: '#ff7875' }}>
                                        {order.priceMultiplier}x 割増料金
                                      </div>
                                    )}
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                      注文日時：{dayjs(order.createdAt).format('MM-DD HH:mm')}
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </List.Item>
                          )}
                        />
                      )}
                    </TabPane>
                  </Tabs>
                </TabPane>

                <TabPane tab="安全设置" key="security">
                  <List
                    itemLayout="horizontal"
                    dataSource={[
                      {
                        title: 'パスワード変更',
                        description: '定期的にパスワードを変更してアカウントを保護しましょう',
                        action: '変更',
                      },
                      {
                        title: '緊急連絡先',
                        description: '緊急連絡先を設定して安全な運転を確保しましょう',
                        action: '設定',
                      },
                      {
                        title: '本人確認',
                        description: user?.status === 'active' ? 'アカウントが有効です' : 'アカウントを有効にしてください',
                        action: user?.status === 'active' ? '有効' : '確認する',
                      },
                    ]}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button
                            type="link"
                            onClick={() => {
                              if (item.title === '緊急連絡先') {
                                setContactModalVisible(true);
                              } else {
                                message.info('機能開発中です');
                              }
                            }}
                          >
                            {item.action}
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<SafetyOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                          title={item.title}
                          description={item.description}
                        />
                      </List.Item>
                    )}
                  />
                </TabPane>

                <TabPane tab="カスタマーサポート" key="support">
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <CustomerServiceOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 20 }} />
                    <h3>お困りですか？</h3>
                    <p style={{ color: '#666', marginBottom: 20 }}>当社のカスタマーサポートチームが24時間体制でサポートいたします</p>
                    <div>
                      <Button type="primary" size="large" style={{ marginRight: 16 }}>
                        オンラインサポート
                      </Button>
                      <Button size="large">
                        電話サポート：03-1234-5678
                      </Button>
                    </div>
                  </div>
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>

        {/* 運転免許証アップロードモーダル */}
        <Modal
          title="運転免許証のアップロード"
          open={uploadModalVisible}
          onCancel={() => setUploadModalVisible(false)}
          footer={null}
        >
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <PlusOutlined />
              </p>
              <p className="ant-upload-text">クリックまたはファイルをドラッグ＆ドロップでアップロード</p>
              <p className="ant-upload-hint">
                運転免許証の表裏の鮮明な写真をアップロードしてください。JPG、PNG形式に対応
              </p>
            </Upload.Dragger>
          </div>
        </Modal>

        {/* 緊急連絡先モーダル */}
        <Modal
          title="緊急連絡先"
          open={contactModalVisible}
          onCancel={() => setContactModalVisible(false)}
          footer={null}
          width={600}
        >
          <List
            header={
              <Button type="primary" icon={<PlusOutlined />}>
                連絡先を追加
              </Button>
            }
            dataSource={emergencyContacts}
            renderItem={(contact) => (
              <List.Item
                actions={[
                  <Button type="link">編集</Button>,
                  <Button type="link" danger>削除</Button>
                ]}
              >
                <List.Item.Meta
                  title={contact.name}
                  description={
                    <div>
                      <div>電話番号：{contact.phone}</div>
                      <div>関係：{contact.relationship}</div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Modal>

        {/* 注文詳細モーダル */}
        <Modal
          title="注文詳細"
          open={orderDetailVisible}
          onCancel={() => setOrderDetailVisible(false)}
          width={700}
          footer={null}
        >
          {selectedOrder && (
            <div>
              {selectedOrder.orderType === 'rental' ? (
                // レンタカー注文詳細
                <div>
                  <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
                    <Descriptions.Item label="注文番号" span={2}>{selectedOrder.id}</Descriptions.Item>
                    <Descriptions.Item label="注文ステータス">
                      <Tag color={getOrderStatusColor(selectedOrder.status)}>
                        {getOrderStatusText(selectedOrder.status)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="車両情報" span={3}>
                      {selectedOrder.carInfo.brand} {selectedOrder.carInfo.model} ({selectedOrder.carInfo.plateNumber})
                    </Descriptions.Item>
                    <Descriptions.Item label="出発日時">{selectedOrder.pickupDate}</Descriptions.Item>
                    <Descriptions.Item label="返却日時">{selectedOrder.returnDate}</Descriptions.Item>
                    <Descriptions.Item label="レンタル日数">
                      {dayjs(selectedOrder.returnDate).diff(dayjs(selectedOrder.pickupDate), 'day')}日間
                    </Descriptions.Item>
                    <Descriptions.Item label="出発地">{selectedOrder.pickupLocation}</Descriptions.Item>
                    <Descriptions.Item label="返却地">{selectedOrder.returnLocation}</Descriptions.Item>
                    <Descriptions.Item label="支払ステータス">
                      <Tag color={selectedOrder.paymentStatus === 'paid' ? 'green' : 'orange'}>
                        {selectedOrder.paymentStatus === 'paid' ? '支払済み' : '未支払'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="レンタル料金">¥{selectedOrder.totalAmount.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="保証金">¥{selectedOrder.deposit.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="注文日時">{selectedOrder.createdAt}</Descriptions.Item>
                  </Descriptions>
                </div>
              ) : (
                // 配車注文詳細
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <Tag color={getServiceTypeColor(selectedOrder.serviceType)}>
                      {getServiceTypeText(selectedOrder.serviceType)}
                    </Tag>
                    <Tag color={getRideStatusColor(selectedOrder.status)}>
                      {getRideStatusText(selectedOrder.status)}
                    </Tag>
                  </div>

                  <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
                    <Descriptions.Item label="注文番号" span={2}>{selectedOrder.id}</Descriptions.Item>
                    <Descriptions.Item label="サービス種別">
                      {getServiceTypeText(selectedOrder.serviceType)}
                    </Descriptions.Item>
                    <Descriptions.Item label="出発地" span={3}>
                      {selectedOrder.pickupLocation.city} - {selectedOrder.pickupLocation.address}
                    </Descriptions.Item>
                    <Descriptions.Item label="目的地" span={3}>
                      {selectedOrder.dropoffLocation.city} - {selectedOrder.dropoffLocation.address}
                    </Descriptions.Item>
                    <Descriptions.Item label="出発日時">{selectedOrder.scheduledTime}</Descriptions.Item>
                    <Descriptions.Item label="乗車人数">{selectedOrder.passengerCount}名</Descriptions.Item>
                    <Descriptions.Item label="支払ステータス">
                      <Tag color={selectedOrder.paymentStatus === 'paid' ? 'green' : 'orange'}>
                        {selectedOrder.paymentStatus === 'paid' ? '支払済み' : '未支払'}
                      </Tag>
                    </Descriptions.Item>
                    {selectedOrder.vehicleType && (
                      <Descriptions.Item label="車種">
                        {selectedOrder.vehicleType === 'business5' ? 'ビジネス5人乗り' : 'ビジネス7人乗り'}
                      </Descriptions.Item>
                    )}
                    {selectedOrder.priceMultiplier && selectedOrder.priceMultiplier > 1 && (
                      <Descriptions.Item label="料金倍率">{selectedOrder.priceMultiplier}x</Descriptions.Item>
                    )}
                    <Descriptions.Item label="合計料金">¥{selectedOrder.totalAmount.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="注文日時">{selectedOrder.createdAt}</Descriptions.Item>
                    {selectedOrder.note && (
                      <Descriptions.Item label="备考" span={3}>{selectedOrder.note}</Descriptions.Item>
                    )}
                  </Descriptions>

                  {selectedOrder.driverInfo && (
                    <Card title="ドライバー情報" size="small">
                      <Descriptions size="small">
                        <Descriptions.Item label="氏名">{selectedOrder.driverInfo.name}</Descriptions.Item>
                        <Descriptions.Item label="電話番号">{selectedOrder.driverInfo.phone}</Descriptions.Item>
                        <Descriptions.Item label="ナンバープレート">{selectedOrder.driverInfo.vehiclePlate}</Descriptions.Item>
                        <Descriptions.Item label="評価">
                          <Rate disabled value={selectedOrder.driverInfo.rating} style={{ fontSize: 14 }} />
                          <span style={{ marginLeft: 8 }}>{selectedOrder.driverInfo.rating}</span>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ProfilePage;